import { useEffect, useRef, useState } from 'react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import _ from 'lodash';
import Cookies from 'js-cookie';
import { IFetchResponse } from '@/types/.fetch.ts';
import { Toast } from 'antd-mobile';
import { setupCache } from 'axios-cache-interceptor';
import { COOKIE_USER_TOKEN } from '@/scripts/const.ts';

export interface IAjaxConfig<T, F> {
    defaultValue?: any; // 默认返回值
    manual?: boolean; // 手动请求
    format?: (data: AxiosResponse<IFetchResponse<T>>) => F; // 返回格式化函数
    throttleWait?: number; // 节流时间（毫秒）
    ignoreErrors?: boolean; // 忽略错误
    cache?: boolean; // 是否启用缓存
    onSuccess?: (data: F) => void; // 成功回调
    onError?: (data: any) => void; // 错误回调
    onFinish?: () => void; // 请求完成回调
}

export interface IAjaxOutput<F> {
    fetch: (extraData?: any, extraCustomConfig?: AxiosRequestConfig) => Promise<F>;
    response: F;
    isLoading: boolean;
    abortController: AbortController | null;
}

const instance = axios.create({
    // @ts-ignore
    baseURL: import.meta.env.VITE_API,
});

const ajax = setupCache(instance);

export default function useAjax<T, F = T>(
    customConfig: AxiosRequestConfig,
    extraConfig?: IAjaxConfig<T, F>,
): IAjaxOutput<F> {
    const [response, setResponse] = useState<F>(extraConfig?.defaultValue);
    const [isLoading, setIsLoading] = useState(!extraConfig?.manual);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const throttlePrev = useRef<any>(0);

    const isThrottleReject = () => {
        if (extraConfig?.throttleWait) {
            const now = Date.now();
            const isReject = now - throttlePrev.current < extraConfig.throttleWait;
            if (!isReject) {
                // 记录当前时间戳
                throttlePrev.current = now;
            }
            return isReject;
        }
        return false;
    };

    const fetch: IAjaxOutput<F>['fetch'] = async (extraData, extraCustomConfig) => {
        const isReject = isThrottleReject();
        if (isReject) {
            return Promise.reject('reject by throttle');
        }
        setIsLoading(true);
        const controller = new AbortController();
        setAbortController(controller);
        const baseConfig = {
            headers: {
                'Content-Type': 'application/json',
            },
            signal: controller.signal,
            method: 'GET',
            cache: extraConfig?.cache || false,
            ...customConfig,
            ...(extraCustomConfig || {}),
        };
        if (extraData) {
            if (!['GET', 'DELETE'].includes(_.upperCase(baseConfig?.method))) {
                if (baseConfig.headers['Content-Type'] === 'multipart/form-data' || _.isArray(extraData)) {
                    // 文件、数组格式不做处理
                    baseConfig.data = extraData;
                } else {
                    baseConfig.data = { ...(baseConfig.data || {}), ...extraData };
                }
            } else {
                baseConfig.params = { ...(baseConfig.params || {}), ...extraData };
            }
        }
        // 追加Token
        baseConfig.headers[COOKIE_USER_TOKEN] = Cookies.get(COOKIE_USER_TOKEN);
        return ajax(baseConfig)
            .then((resp) => {
                // 避免文件流报错
                if (resp.headers['content-type'].indexOf('application/json') > -1) {
                    if (resp.data.code === 1) {
                        return Promise.reject('requireLogin');
                    } else if (resp.data.code !== 0 && resp.data.code !== 1) {
                        return Promise.reject(resp.data.msg);
                    }
                }
                let result;
                // 如果存在格式化函数，则进行格式化
                if (extraConfig?.format) {
                    result = extraConfig.format(resp);
                } else {
                    result = resp.data.data;
                }
                setResponse(result);
                // 成功回调
                extraConfig?.onSuccess?.(result);
                return Promise.resolve(result);
            })
            .finally(() => {
                setIsLoading(false);
                extraConfig?.onFinish?.();
            })
            .catch(async (err) => {
                if (!axios.isCancel(err)) {
                    if (!extraConfig?.ignoreErrors) {
                        switch (err) {
                            case 'requireLogin':
                                localStorage.clear();
                                window.location.reload();
                                break;
                            default:
                                Toast.show({
                                    content: _.isString(err) ? err : '请求出了点问题',
                                });
                        }
                    }
                    extraConfig?.onError?.(err);
                    return Promise.reject(err);
                }
            });
    };

    useEffect(() => {
        if (!extraConfig?.manual) {
            fetch();
        }
    }, []);

    return { fetch, response, isLoading, abortController };
}
